import React, { useState, createContext, useContext, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('rozedinapoli_user') || 'null');
    } catch (e) {
      localStorage.removeItem('rozedinapoli_user');
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Keep in sync if another tab logs in/out
    const onStorage = (e) => {
      if (e.key === 'rozedinapoli_user') {
        try { setUser(e.newValue ? JSON.parse(e.newValue) : null); } catch (err) { setUser(null); }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const user = response.data;
      setUser(user);
      localStorage.setItem('rozedinapoli_user', JSON.stringify(user));
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      const user = response.data;
      setUser(user);
      localStorage.setItem('rozedinapoli_user', JSON.stringify(user));
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('rozedinapoli_user');
  };

  const getUserOrders = async () => {
    if (!user) return [];
    
    try {
      const response = await axios.get(`${API}/auth/orders/${user.id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user orders:', error);
      return [];
    }
  };

  const updateProfile = async (profileData) => {
    if (!user) return { success: false, error: 'Not logged in' };
    
    try {
      const response = await axios.put(`${API}/auth/profile/${user.id}`, profileData);
      const updatedUser = response.data;
      setUser(updatedUser);
      localStorage.setItem('rozedinapoli_user', JSON.stringify(updatedUser));
      return { success: true, user: updatedUser };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Profile update failed' 
      };
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    getUserOrders,
    updateProfile,
    isLoggedIn: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;